import React, { Dispatch, SetStateAction, useMemo } from 'react';

import { Cross as CrossSVG } from '@digital-hig/icon';
import AlertIcon from '@digital-hig/mui/AlertIcon';
import Snackbar from '@digital-hig/mui/Snackbar';
import SvgIcon from '@digital-hig/mui/SvgIcon';
import Typography from '@digital-hig/mui/Typography';
import Slide, { SlideProps } from '@mui/material/Slide';
import { SnackbarProps } from '@mui/material/Snackbar';
import { SnackbarCloseReason } from '@mui/material/Snackbar/Snackbar';

const defaultValue: SnackbarProps = {
  open: false,
  message: '',
  onClose: () => {},
  action: undefined,
  key: undefined,
};

interface ToastContextInterface {
  currentToast: SnackbarProps;
  closeCurrentToast: () => void;
  setToast: Dispatch<SetStateAction<SnackbarProps>>;
  setErrorToast: (toast: SnackbarProps) => void;
  setSuccessToast: (toast: SnackbarProps) => void;
  setInfoToast: (toast: SnackbarProps) => void;
  setWarningToast: (toast: SnackbarProps) => void;
}
export const ToastContext = React.createContext<ToastContextInterface>({
  currentToast: defaultValue,
  closeCurrentToast: () => {},
  setToast: prevState => prevState,
  setErrorToast: () => {},
  setSuccessToast: () => {},
  setInfoToast: () => {},
  setWarningToast: () => {},
});

export function ToastContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [currentToast, setToast] = React.useState<SnackbarProps>(defaultValue);

  const closeCurrentToast = () => {
    setToast(prev => ({ ...prev, open: false }));
  };

  const setErrorToast = ({ message, ...otherSettings }: SnackbarProps) => {
    setToast({
      open: true,
      message: (
        <>
          <AlertIcon severity="error" variant="standard" />
          <Typography variant="short-copy-small">{message}</Typography>
        </>
      ),
      ...otherSettings,
    });
  };
  const setSuccessToast = ({ message, ...otherSettings }: SnackbarProps) => {
    setToast({
      open: true,
      message: (
        <>
          <AlertIcon severity="success" variant="standard" />
          <Typography variant="short-copy-small">{message}</Typography>
        </>
      ),
      ...otherSettings,
    });
  };

  const setInfoToast = ({ message, ...otherSettings }: SnackbarProps) => {
    setToast({
      open: true,
      message: (
        <>
          <AlertIcon severity="info" variant="standard" />
          <Typography variant="short-copy-small">{message}</Typography>
        </>
      ),
      ...otherSettings,
    });
  };

  const setWarningToast = ({ message, ...otherSettings }: SnackbarProps) => {
    setToast({
      open: true,
      message: (
        <>
          <AlertIcon severity="warning" variant="standard" />
          <Typography variant="short-copy-small">{message}</Typography>
        </>
      ),
      ...otherSettings,
    });
  };

  const state = useMemo(
    () => ({
      currentToast,
      setToast,
      setErrorToast,
      setSuccessToast,
      setInfoToast,
      setWarningToast,
      closeCurrentToast,
    }),
    [currentToast, setToast]
  );

  const handleClose = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: React.SyntheticEvent<any> | Event,
    reason: SnackbarCloseReason
  ) => {
    if (currentToast.onClose) {
      currentToast.onClose(event, reason);
    }

    if (reason === 'clickaway') {
      return;
    }

    setToast(prev => ({ ...prev, open: false }));
  };

  return (
    <ToastContext.Provider value={state}>
      <Snackbar
        autoHideDuration={currentToast.autoHideDuration ?? 6000}
        TransitionComponent={Slide as React.ComponentType<SlideProps>}
        TransitionProps={{
          onExited: closeCurrentToast,
        }}
        onClose={handleClose}
        action={
          currentToast.action ?? (
            <SvgIcon onClick={handleClose} tabIndex="0">
              <CrossSVG />
            </SvgIcon>
          )
        }
        {...currentToast}
      />
      {children}
    </ToastContext.Provider>
  );
}
